import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { MuiTelInput } from 'mui-tel-input';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { RootState } from '@/redux/store';
import { Countries } from '@/utils/common';
import { getError } from '@/utils/hookFormUtils';

import type { CreateOrderFormType } from '../create-order-schema';
import type {
  Control,
  FieldErrors,
  FieldPath,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';

interface UserDataProps {
  label: string;
  name: FieldPath<CreateOrderFormType>;
  Icon: React.ReactNode;
  placeholder?: string;
}

interface UserDataBoxProps {
  handleChangeSelectedCountryPhone: (country: string) => void;
  userData: readonly UserDataProps[];
  register: UseFormRegister<CreateOrderFormType>;
  errors: FieldErrors<CreateOrderFormType>;
  control: Control<CreateOrderFormType>;
  watch: UseFormWatch<CreateOrderFormType>;
}

// Mapea los códigos de país del backend (MEX, COL, CHL, PER) a códigos ISO (MX, CO, CL, PE)
const mapCountryCode = (country?: Countries): 'MX' | 'CO' | 'CL' | 'PE' => {
  const countryMap: Record<Countries, 'MX' | 'CO' | 'CL' | 'PE'> = {
    MEX: 'MX',
    COL: 'CO',
    CHL: 'CL',
    PER: 'PE',
  };

  return country ? countryMap[country] : 'MX';
};

const UserDataBox = (props: UserDataBoxProps) => {
  const { handleChangeSelectedCountryPhone, userData, register, errors, control, watch } = props;

  const { profile } = useSelector((state: RootState) => state.parcel);
  const userCountry = profile?.data?.country;
  const defaultCountry = mapCountryCode(userCountry);

  return (
    <Grid container size={{ xxs: 12 }} spacing={2}>
      {userData.map((data: UserDataProps) => {
        const error = getError(errors, data.name);
        return data.label !== 'Teléfono' ? (
          <Grid size={{ xxs: 12, xl: 6 }} key={data.name}>
            <TextField
              {...register(data.name)}
              value={watch(data.name) || ''}
              size="small"
              variant="outlined"
              fullWidth
              label={data.label}
              placeholder={data.placeholder}
              error={!!error}
              helperText={error?.message ? String(error.message) : undefined}
              slotProps={{
                htmlInput: {
                  autoComplete: 'off',
                  role: 'presentation',
                  'data-form-type': 'other',
                  'data-lpignore': 'true',
                  'data-1p-ignore': 'true',
                },
                input: {
                  startAdornment: data.Icon ? (
                    <InputAdornment position="start" sx={{ color: 'primary.main' }}>
                      {data.Icon}
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Grid>
        ) : (
          <Grid size={{ xxs: 12, xl: 6 }} key={data.name}>
            <Controller
              name={data.name}
              control={control}
              render={({ field }) => {
                const phoneError = getError(errors, data.name);
                return (
                  <MuiTelInput
                    {...field}
                    value={(field.value as string) || ''}
                    sx={{ width: '100%' }}
                    size="small"
                    variant="outlined"
                    label="Teléfono"
                    onlyCountries={['MX', 'PE', 'CO', 'CL']}
                    defaultCountry={defaultCountry}
                    autoComplete="off"
                    slotProps={{
                      htmlInput: {
                        autoComplete: 'off',
                        role: 'presentation',
                        'data-form-type': 'other',
                        'data-lpignore': 'true',
                        'data-1p-ignore': 'true',
                      },
                    }}
                    error={!!phoneError}
                    helperText={phoneError?.message ? String(phoneError.message) : undefined}
                    onChange={(e, country) => {
                      field.onChange(e.replace(/\s/g, ''));
                      handleChangeSelectedCountryPhone(country.countryCode!);
                    }}
                  />
                );
              }}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default UserDataBox;
